import { useEffect, useRef, useState } from "react";
import { AnchorLinkItem } from "./AnchorLinks";

/**
 * Finds anchor elements in DOM and saves element ref in state.
 */
function useAnchorElements(anchorLinks: AnchorLinkItem[]) {
  const [anchorElements, setAnchorElements] = useState<
    (HTMLAnchorElement | null)[]
  >([]);
  useEffect(() => {
    const list = anchorLinks.map((x) => {
      if (!x.id) return null;
      return document.querySelector(`#${x.id}`) as HTMLAnchorElement;
    });
    setAnchorElements(list);
  }, [anchorLinks]);
  return anchorElements;
}

/*
 *  Inspiration:
 *  https://github.com/reactjs/reactjs.org/blob/main/beta/src/components/Layout/useTocHighlight.tsx
 */
export function useActiveAnchor(
  anchorLinks: AnchorLinkItem[],
  isDesktop: boolean
) {
  const [activeIndex, setCurrentIndex] = useState<number>(0);
  const timeoutRef = useRef<number | null>(null);

  const anchorElements = useAnchorElements(anchorLinks);

  useEffect(() => {
    const updateActiveLink = () => {
      // 56 = sticky anchor links. 56*2 anchor links + sticky header
      const topOffset = (isDesktop ? 56 : 56 * 2) + 1;

      let index = -1;
      /* loop until we find a section that is scrolled to the top (over above),
       * the index before will be the one we count as active.
       */
      while (index < anchorElements.length - 1) {
        const headerAnchor = anchorElements[index + 1];
        if (headerAnchor) {
          const { top } = headerAnchor.getBoundingClientRect();
          const isAtTop = top - topOffset < 0;
          if (!isAtTop) {
            break;
          }
        }
        index += 1;
      }

      setCurrentIndex(Math.max(index, 0));
    };

    updateActiveLink();

    const throttledUpdateActiveLink = () => {
      if (timeoutRef.current === null) {
        timeoutRef.current = window.setTimeout(() => {
          timeoutRef.current = null;
          updateActiveLink();
        }, 100);
      }
    };
    document.addEventListener("scroll", throttledUpdateActiveLink, {
      passive: true,
    });
    document.addEventListener("resize", throttledUpdateActiveLink, {
      passive: true,
    });

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      document.removeEventListener("scroll", throttledUpdateActiveLink);
      document.removeEventListener("resize", throttledUpdateActiveLink);
    };
  }, [anchorElements, isDesktop]);

  return { activeIndex };
}
